import { snakeCase, uniqBy } from 'lodash';
import {
  MetaModelComponentTypeWithNullTypes,
  MetaModelReferenceTypeWithNullTypes,
  MetaModelWithNullTypes,
  isMetaModelComponentType,
  isMetaModelReferenceType,
  isMetaModelTriple,
} from './types';
import { NULL_TYPE_NAME } from './consts';
import {
  ArdoqId,
  ArrowType,
  DirectedTripleWithFilters,
  LineType,
  MetaModel,
  MetaModelComponentType,
  MetaModelReferenceType,
  MetaModelTriple,
  PARENT_REF_TYPE_NAME,
  VIRTUAL_PARENT_REF_TYPE_NAME,
} from '@ardoq/api-types';
import { Locale, localeCompare } from '@ardoq/locale';
import { isArdoqError } from '@ardoq/common-helpers';
import {
  LoadMetaModelAsScopedDataState,
  MetaModelWithContextLoadedState,
} from 'viewpointBuilder/metaModel/loadMetaModelTypes';
import { MetaModelLoadedState } from './types';
import { colorsAndRepresentations } from '@ardoq/scope-data';
import { ArdoqIcon } from '@ardoq/data-model';
import { getIcon } from '@ardoq/icons';
import {
  ensureContrast,
  getDefaultCSSColor,
  getLightenedColor,
  getShadedColor,
} from '@ardoq/color-helpers';
import {
  RawGraphEdge,
  RawGraphEdges,
  RawGraphNode,
  RawGraphNodes,
} from 'viewpointBuilder/types';
import { ComponentRepresentationDataWithColorShades } from '@ardoq/basic-components';

const getTypeNameToId = <K>(types: { name: string; id: K }[]) => {
  const map = new Map(types.map(({ name, id }) => [name, id]));
  return (name: string) => {
    const typeId = map.get(name);
    if (typeId === undefined) {
      throw Error(`No type id for type name '${name}'`);
    }
    return typeId;
  };
};

const checkAndFixMetaModel = ({
  componentTypes,
  referenceTypes,
  triples,
}: MetaModelWithNullTypes): MetaModel => ({
  componentTypes: componentTypes.map(componentType =>
    isMetaModelComponentType(componentType)
      ? componentType
      : fixComponentType(componentType)
  ),
  referenceTypes: referenceTypes.map(referenceType =>
    isMetaModelReferenceType(referenceType)
      ? referenceType
      : fixReferenceType(referenceType)
  ),
  triples: triples.map(triple =>
    isMetaModelTriple(triple)
      ? triple
      : {
          sourceComponentType: triple.sourceComponentType ?? NULL_TYPE_NAME,
          targetComponentType: triple.targetComponentType ?? NULL_TYPE_NAME,
          referenceType: triple.referenceType ?? NULL_TYPE_NAME,
        }
  ),
});

const fixReferenceType = (
  referenceType: MetaModelReferenceTypeWithNullTypes
): MetaModelReferenceType => ({
  ...referenceType,
  name: referenceType.name ?? NULL_TYPE_NAME,
  style: {
    ...referenceType.style,
    color: referenceType.style.color ?? 'black',
    line: referenceType.style.line ?? LineType.SOLID,
    lineEnding: referenceType.style.lineEnding ?? ArrowType.BOTH,
  },
});

const fixComponentType = (
  componentType: MetaModelComponentTypeWithNullTypes
): MetaModelComponentType => ({
  ...componentType,
  name: componentType.name ?? NULL_TYPE_NAME,
  style: {
    ...componentType.style,
    level: componentType.style.level ?? 0,
  },
});

const typeNameToWorkspaceAndFieldMaps = (metaModel: MetaModel) => {
  const typeNameToWorkspaceMap = new Map(
    metaModel.componentTypes.map(({ name, usedInWorkspaceIds }) => [
      name,
      usedInWorkspaceIds,
    ])
  );
  const componentTypeNameToFieldMap = new Map(
    metaModel.componentTypes.map(({ name, fieldNames }) => [name, fieldNames])
  );
  const referenceTypeNameToFieldMap = new Map(
    metaModel.referenceTypes.map(({ name, fieldNames }) => [name, fieldNames])
  );
  return {
    typeNameToWorkspaceMap,
    componentTypeNameToFieldMap,
    referenceTypeNameToFieldMap,
  };
};

export const toTripleKey = ({
  sourceComponentType,
  referenceType,
  targetComponentType,
}: MetaModelTriple): string =>
  [sourceComponentType, referenceType, targetComponentType].join('');

/**
 * A triple key that compares equal whichever spelling of the parent reference
 * type a path carries, since `extendMetamodelWithTriples` stores the
 * metamodel's name.
 */
const toNormalizedTripleKey = (triple: MetaModelTriple): string =>
  toTripleKey({
    ...triple,
    referenceType:
      triple.referenceType === VIRTUAL_PARENT_REF_TYPE_NAME
        ? PARENT_REF_TYPE_NAME
        : triple.referenceType,
  });

const extendMetamodelWithTriples = (
  { componentTypes, referenceTypes, triples }: MetaModel,
  additionalTriples: MetaModelTriple[],
  locale: Locale
): MetaModel => {
  const metamodelComponentTypeNames = new Set(
    componentTypes.map(({ name }) => name)
  );
  const metamodelReferenceTypeNames = new Set(
    referenceTypes.map(({ name }) => name)
  );

  const additionalTriplesNormalized = additionalTriples.map(triple => ({
    ...triple,
    referenceType:
      VIRTUAL_PARENT_REF_TYPE_NAME === triple.referenceType
        ? PARENT_REF_TYPE_NAME
        : triple.referenceType,
  }));

  const additionalComponentTypes = additionalTriplesNormalized
    .flatMap(({ sourceComponentType, targetComponentType }) => [
      sourceComponentType,
      targetComponentType,
    ])
    .filter(name => !metamodelComponentTypeNames.has(name))
    .map(toComponentFallbackType);

  const additionalReferenceTypes = additionalTriplesNormalized
    .map(({ referenceType }) => referenceType)
    .filter(name => !metamodelReferenceTypeNames.has(name))
    .map(toReferenceFallbackType);

  const sortByName = <T extends { name: string }>(
    { name: nameA }: T,
    { name: nameB }: T
  ) => localeCompare(nameA, nameB, locale);

  // Sorting the metamodel to make it comparable with `isEqual` with another
  // metamodel.
  return {
    componentTypes: [...componentTypes, ...additionalComponentTypes]
      .map(toSortedTypes)
      .sort(sortByName),
    referenceTypes: [...referenceTypes, ...additionalReferenceTypes]
      .map(toSortedTypes)
      .sort(sortByName),
    triples: uniqBy(
      [...triples, ...additionalTriplesNormalized],
      toTripleKey
    ).sort(sortTriples(locale)),
  };
};

const toSortedTypes = <
  T extends { usedInWorkspaceIds: ArdoqId[]; fieldNames: string[] },
>(
  type: T
) => ({
  ...type,
  usedInWorkspaceIds: type.usedInWorkspaceIds.sort(),
  fieldNames: type.fieldNames.sort(),
});

const sortTriples =
  (locale: Locale) => (tripleA: MetaModelTriple, tripleB: MetaModelTriple) => {
    if (tripleA.sourceComponentType !== tripleB.sourceComponentType) {
      return localeCompare(
        tripleA.sourceComponentType,
        tripleB.sourceComponentType,
        locale
      );
    }
    if (tripleA.referenceType !== tripleB.referenceType) {
      return localeCompare(
        tripleA.referenceType,
        tripleB.referenceType,
        locale
      );
    }
    return localeCompare(
      tripleA.targetComponentType,
      tripleB.targetComponentType,
      locale
    );
  };

const toComponentFallbackType = (name: string): MetaModelComponentType => ({
  name,
  style: {
    color: 'blue',
    icon: 'None',
    image: null,
    level: 0,
    shape: null,
    standard: null,
  },
  usedInWorkspaceIds: [],
  fieldNames: [],
});

const toReferenceFallbackType = (name: string): MetaModelReferenceType => ({
  name,
  style: {
    color: 'black',
    line: LineType.SOLID,
    lineBeginning: ArrowType.NONE,
    lineEnding: ArrowType.BOTH,
    svgStyle: null,
  },
  usedInWorkspaceIds: [],
  fieldNames: [],
});

const getTriplesFromPaths = (
  paths: DirectedTripleWithFilters[][]
): MetaModelTriple[] => {
  return uniqBy(
    paths.flatMap(path =>
      path.map(({ sourceType, targetType, referenceType }) => ({
        sourceComponentType: sourceType,
        referenceType,
        targetComponentType: targetType,
      }))
    ),
    toTripleKey
  );
};

const processMetaModel = (
  metaModelResult: MetaModelLoadedState
): LoadMetaModelAsScopedDataState => {
  if (isArdoqError(metaModelResult)) {
    return {
      error: metaModelResult.error.message,
      status: 'ERROR',
    };
  }

  const {
    typeNameToWorkspaceMap,
    componentTypeNameToFieldMap,
    referenceTypeNameToFieldMap,
  } = metaModelOperations.typeNameToWorkspaceAndFieldMaps(metaModelResult);

  return {
    referenceTypes: metaModelResult.referenceTypes,
    componentTypes: metaModelResult.componentTypes,
    componentNameAndFieldsIndex: componentTypeNameToFieldMap,
    referenceNameAndFieldsIndex: referenceTypeNameToFieldMap,
    componentNameAndWorkspacesIndex: typeNameToWorkspaceMap,
    triples: metaModelResult.triples,
    status: 'DATA_LOADED',
  } satisfies MetaModelWithContextLoadedState;
};

const getMetamodelExtendedWithTriplesInPaths = (
  metaModel: MetaModel,
  paths: DirectedTripleWithFilters[][],
  locale: Locale
) => extendMetamodelWithTriples(metaModel, getTriplesFromPaths(paths), locale);

const getComponentRepresentationDataWithTypeName = (
  componentTypeName: string,
  componentTypeNameMap: Map<string, MetaModelComponentType>
): {
  icon: ArdoqIcon;
  isImage: boolean;
  value: string;
} => {
  const componentType = componentTypeNameMap.get(componentTypeName);
  if (!componentType) {
    return colorsAndRepresentations.getFallbackComponentRepresentation();
  }
  const value = componentType.style.image || componentType.style.icon || '';
  return {
    value,
    isImage: Boolean(componentType.style.image),
    icon: getIcon(value),
  };
};

const getComponentTypeRepresentationDataWithColorShades = (
  metaModel: MetaModel,
  typeName: string,
  componentTypeNameMap = new Map(
    metaModel.componentTypes.map(type => [type.name, type])
  )
): ComponentRepresentationDataWithColorShades => {
  const baseRepresentationData = getComponentRepresentationDataWithTypeName(
    typeName,
    componentTypeNameMap
  );
  const color = getComponentColorWithTypeName(typeName, componentTypeNameMap);
  const shadedColor = getShadedColor(color);
  const lightenedColor = getLightenedColor(color);
  const contrastColor = ensureContrast(lightenedColor, color);
  return {
    ...baseRepresentationData,
    color,
    shadedColor,
    lightenedColor,
    contrastColor,
  };
};

const getComponentColorWithTypeName = (
  componentTypeName: string,
  componentTypeNameMap: Map<string, MetaModelComponentType>
): string => {
  const componentType = componentTypeNameMap.get(componentTypeName);
  if (!componentType) {
    return colorsAndRepresentations.DEFAULT_COLOR;
  }
  return (
    componentType.style.color || getDefaultCSSColor(componentType.style.level)
  );
};

const getReferenceRepresentationDataWithTypeName = (
  referenceTypeName: string,
  displayLabel: string,
  referenceTypeNameMap: Map<string, MetaModelReferenceType>
) => {
  const referenceType = referenceTypeNameMap.get(referenceTypeName);
  if (!referenceType) {
    return {
      color: colorsAndRepresentations.DEFAULT_COLOR,
      line: LineType.SOLID,
      lineBeginning: ArrowType.NONE,
      lineEnding: ArrowType.BOTH,
      displayLabel,
    };
  }
  return {
    color: referenceType.style.color,
    line: referenceType.style.line,
    lineBeginning: referenceType.style.lineBeginning,
    lineEnding: referenceType.style.lineEnding,
    displayLabel,
  };
};

const createRawGraphNodes = (metaModel: MetaModel): RawGraphNodes => {
  const componentTypeNameMap = new Map(
    metaModel.componentTypes.map(type => [type.name, type])
  );

  return new Map(
    metaModel.componentTypes.map(componentType => {
      const rawGraphNode = createRawGraphNode(
        metaModel,
        componentType,
        componentTypeNameMap
      );
      return [rawGraphNode.modelId, rawGraphNode];
    })
  );
};

const createRawGraphNode = (
  metaModel: MetaModel,
  componentType: MetaModelComponentType,
  componentTypeNameMap: Map<string, MetaModelComponentType>
): RawGraphNode => {
  const name = componentType.name;
  const representationData = getComponentTypeRepresentationDataWithColorShades(
    metaModel,
    name,
    componentTypeNameMap
  );

  return {
    modelId: name,
    label: name,
    metaData: {
      representationData,
      color: representationData.color,
      typeId: snakeCase(name),
      label: name,
    },
  };
};

const createRawGraphEdges = (metaModel: MetaModel): RawGraphEdges => {
  const referenceTypeNameMap = new Map(
    metaModel.referenceTypes.map(type => [type.name, type])
  );

  return new Map(
    metaModel.triples.map(triple => {
      const rawGraphEdge = createRawGraphEdge(triple, referenceTypeNameMap);
      return [rawGraphEdge.modelId, rawGraphEdge];
    })
  );
};

const createRawGraphEdge = (
  { sourceComponentType, referenceType, targetComponentType }: MetaModelTriple,
  referenceTypeNameMap: Map<string, MetaModelReferenceType>
): RawGraphEdge => {
  const representationData = getReferenceRepresentationDataWithTypeName(
    referenceType,
    referenceType,
    referenceTypeNameMap
  );

  return {
    modelId: getRawGraphEdgeId(
      sourceComponentType,
      referenceType,
      targetComponentType
    ),
    label: referenceType,
    sourceId: sourceComponentType,
    targetId: targetComponentType,
    metaData: {
      representationData,
    },
  };
};

const getRawGraphEdgeId = (
  sourceComponentType: string,
  referenceType: string,
  targetComponentType: string
) => `${sourceComponentType}__${referenceType}__${targetComponentType}`;

export const metaModelOperations = {
  checkAndFixMetaModel,
  getMetamodelExtendedWithTriplesInPaths,
  toNormalizedTripleKey,
  getTypeNameToId,
  typeNameToWorkspaceAndFieldMaps,
  extendMetamodelWithTriples,
  getTriplesFromPaths,
  processMetaModel,
  getComponentRepresentationDataWithTypeName,
  getComponentTypeRepresentationDataWithColorShades,
  getComponentColorWithTypeName,
  getReferenceRepresentationDataWithTypeName,
  createRawGraphNodes,
  createRawGraphEdges,
  getRawGraphEdgeId,
};
